import Link from 'next/link';
import classNames from 'classnames';

interface BaseProps {
  size?: Size;
  variant?: IconButtonVariant;
  children: React.ReactNode;
  className?: string;
}

interface ButtonProps extends BaseProps {
  disabled?: boolean;
  onClick: () => void;
}

interface LinkProps extends BaseProps {
  href: string;
  target?: '_blank';
}

type Size = 'small' | 'large';

export type IconButtonVariant = 'solid' | 'ghost';

const variantClasses: Record<IconButtonVariant, string> = {
  solid: 'border border-cf-gray-5 bg-cf-gray-4',
  ghost: 'border border-transparent',
};

const IconButton: React.FC<ButtonProps | LinkProps> = ({
  size = 'large',
  variant = 'solid',
  children,
  className,
  ...props
}) => {
  if ('onClick' in props) {
    const handleClick = (event: React.SyntheticEvent) => {
      if (!props.disabled) {
        event.stopPropagation();
        props.onClick();
      }
    };

    return (
      <div
        role="button"
        tabIndex={0}
        className={classNames(
          size === 'small' ? 'h-5 w-5 min-w-[20px]' : 'h-6 w-6 min-w-[24px]',
          'flex h-6 w-6 items-center justify-center rounded text-cf-light-3 outline-none transition',
          variantClasses[variant],
          !props.disabled && 'hover:bg-cf-gray-5 hover:text-cf-light-4',
          className,
        )}
        onClick={handleClick}
      >
        {children}
      </div>
    );
  }

  return (
    <Link
      href={props.href}
      target={props.target}
      className={classNames(
        size === 'small' ? 'h-5 w-5 min-w-[20px]' : 'h-6 w-6 min-w-[24px]',
        'flex h-6 w-6 items-center justify-center rounded text-cf-light-3 outline-none transition',
        variantClasses[variant],
        'hover:bg-cf-gray-5 hover:text-cf-light-4',
        className,
      )}
    >
      {children}
    </Link>
  );
};

export default IconButton;
